/**
 * MultiSiteConsolePage — Foreman/Admin view of all sites, shifts, channels.
 * Create/manage sites, shifts, and view handoff logs.
 */
import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Plus, RefreshCw, Clock, Radio, FileText, Trash2, Shield, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../AuthContext";

function SiteCard({ site, onDelete }) {
  const [channels, setChannels] = useState([]);
  const [shifts, setShifts]     = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);

  const load = async () => {
    if (!open) {
      setLoading(true);
      const [chs, shs] = await Promise.all([
        api.getChannels(site.site_id),
        api.getShifts(site.site_id),
      ]).catch(() => [[], []]);
      setChannels(chs);
      setShifts(shs);
      setLoading(false);
    }
    setOpen((o) => !o);
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
      <button onClick={load} className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-800/50 transition-colors">
        <MapPin className="w-5 h-5 text-indigo-400 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-white">{site.name}</h3>
            {site.address && <span className="text-xs text-gray-500 truncate">{site.address}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); onDelete(site.site_id); }}
            className="p-1 text-red-500/60 hover:text-red-400 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
          {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-800 p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {loading ? <div className="col-span-2 flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-indigo-400" /></div> : (
            <>
              {/* Channels */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Radio className="w-3 h-3" /> Channels ({channels.length})
                </p>
                {channels.length === 0 ? <p className="text-xs text-gray-600">No channels</p> : (
                  <div className="space-y-1">
                    {channels.map((ch) => (
                      <div key={ch.channel_id} className="bg-gray-800 rounded px-2.5 py-1.5 text-xs text-gray-300">
                        #{ch.name}
                        {ch.shift_id && <span className="text-gray-600 ml-1">· shift-linked</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Shifts */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Shifts ({shifts.length})
                </p>
                {shifts.length === 0 ? <p className="text-xs text-gray-600">No shifts</p> : (
                  <div className="space-y-1.5">
                    {shifts.map((sh) => (
                      <div key={sh.shift_id} className="bg-gray-800 rounded px-2.5 py-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-white font-medium">{sh.name}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            sh.status === "active" ? "bg-green-500/20 text-green-400" : "bg-gray-700 text-gray-500"
                          }`}>{sh.status}</span>
                        </div>
                        {sh.handoff_log?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> {sh.handoff_log.length} handoff note(s)
                          </p>
                        )}
                        {!sh.foreman_id && (
                          <p className="text-xs text-yellow-500 mt-0.5">⚠ No foreman assigned</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CreateSiteModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", address: "", description: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const s = await api.createSite(form);
      onCreated(s);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="font-semibold text-white">Add Site</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-lg leading-none">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Site Name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
              placeholder="e.g. Warehouse A" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
              placeholder="123 Main St, City, State" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 rounded-lg text-sm bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50">
              {saving ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MultiSiteConsolePage() {
  const { user } = useAuth();
  const [sites, setSites]         = useState([]);
  const [stats, setStats]         = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading]     = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [ss, st] = await Promise.all([api.getSites(), api.getStats()]).catch(() => [[], null]);
    setSites(ss);
    setStats(st);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSite = async (id) => {
    if (!window.confirm("Delete this site and all its channels?")) return;
    await api.deleteSite(id);
    setSites((prev) => prev.filter((s) => s.site_id !== id));
  };

  if (!user || (user.role !== "admin" && user.role !== "employer")) {
    return <div className="flex-1 flex items-center justify-center"><p className="text-gray-400">Foreman/Admin access required.</p></div>;
  }

  const StatPill = ({ label, value, color = "text-indigo-400" }) => (
    <div className="bg-gray-900 border border-gray-800 rounded-xl px-4 py-3 text-center">
      <p className={`text-2xl font-bold ${color}`}>{value ?? "—"}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <Shield className="w-5 h-5 text-indigo-400" /> Multi-Site Console
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-gray-400 hover:text-white p-1.5 rounded-lg hover:bg-gray-800">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-500">
            <Plus className="w-4 h-4" /> Add Site
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatPill label="Sites"           value={stats.total_sites} />
          <StatPill label="Active Shifts"   value={stats.active_shifts}  color="text-green-400" />
          <StatPill label="Online Users"    value={stats.online_users}   color="text-yellow-400" />
          <StatPill label="Total Messages"  value={stats.total_messages} color="text-purple-400" />
        </div>
      )}

      {/* Sites */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>
      ) : sites.length === 0 ? (
        <div className="text-center py-16">
          <MapPin className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No sites yet</p>
          <button onClick={() => setShowCreate(true)} className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
            Add first site
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map((site) => (
            <SiteCard key={site.site_id} site={site} onDelete={deleteSite} />
          ))}
        </div>
      )}

      {showCreate && <CreateSiteModal onClose={() => setShowCreate(false)} onCreated={(s) => { setSites((p) => [s, ...p]); setShowCreate(false); }} />}
    </div>
  );
}
